import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Modal,
  IconButton,
  Button,
} from '@mui/material';
import { Assignment as AssignmentIcon, Visibility as VisibilityIcon, Close as CloseIcon } from '@mui/icons-material';

const TeacherHomework = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const classId = sessionStorage.getItem('classId');
    const sectionId = sessionStorage.getItem('sectionId');

    if (!token || !classId || !sectionId) {
      setError('Missing authentication or class details.');
      setLoading(false);
      return;
    }

    const fetchHomeworkList = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Homework/ListByClass`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, sectionId }),
        });

        if (response.ok) {
          const responseData = await response.json();
          setHomeworks(Array.isArray(responseData.homework) ? responseData.homework : []);
          setFilteredData(Array.isArray(responseData.homework) ? responseData.homework : []);
        } else {
          setError('Failed to fetch homework list');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch homework list');
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworkList();
  }, []);

  const handleView = (homework) => {
    setSelectedHomework(homework);
    setShowViewModal(true);
  };

  const handleClose = () => {
    setShowViewModal(false);
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      <Card sx={{ maxWidth: '100%', mx: 'auto', boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'rgba(75,108,183,1)', mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgba(49, 70, 119, 1)' }}>
              Homework
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 0, border: '1px solid #e0e0e0' }}>
              <Table sx={{ minWidth: 650 }} aria-label="homework">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>S.No</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Description</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Homework Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Submission Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2, textAlign: 'center' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((homework, index) => (
                      <TableRow
                        key={homework.homeworkId}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' },
                          '&:hover': { bgcolor: '#f0f0f0' },
                        }}
                      >
                        <TableCell sx={{ p: 2, textAlign: 'center' }}>{index + 1}</TableCell>
                        <TableCell sx={{ p: 2 }}>{homework.caption}</TableCell>
                        <TableCell sx={{ p: 2, textAlign: 'center' }}>
                          {new Date(homework.homeworkDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ p: 2, textAlign: 'center' }}>
                          {new Date(homework.dueDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ p: 2, textAlign: 'center' }}>
                          <Button
                            variant="contained"
                            size="small"
                            color="info"
                            onClick={() => handleView(homework)}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              textTransform: 'none',
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        No Homeworks Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* View Homework Modal */}
      <Modal open={showViewModal} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 600 },
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: '1.1rem' }}>
              View Homework
            </Typography>
          </Box>
          <Box sx={{ padding: '24px 20px', overflowX: 'hidden', overflowY: 'auto' }}>
            <Typography sx={{ mb: 3, fontSize: '1rem', color: '#495057', lineHeight: 1.6 }}>
              Homework:<br /><strong>{selectedHomework?.caption}</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3, maxHeight: '300px', overflow: 'auto' }}>
              {selectedHomework?.files?.length > 0 ? (
                selectedHomework.files.map((file) => (
                <Box
  key={file.fileId}
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 1.5,
    backgroundColor: '#f8f9fa',
    borderRadius: 1,
  }}
>
  <Button
    variant="contained"
    onClick={() =>
      window.open(
        `${process.env.REACT_APP_DOCUMENT_URL}${file.filePath}`,
        '_blank'
      )
    }
    sx={{
      fontWeight: 600,
      textTransform: 'none',
      backgroundColor: '#459ec4',
      color: '#fff',
      borderRadius: 1,
      padding: '8px 16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        backgroundColor: '#3a8bbb',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      },
    }}
  >
    {file.originalFileName}
  </Button>
</Box>

                ))
              ) : (
                <Typography variant="body2" sx={{ color: '#666' }}>
                  No files attached.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TeacherHomework;
